import { useEffect } from "preact/hooks";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — webhookstorage";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Privacy policy for webhookstorage — data collection, retention, subprocessors, and your rights.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://webhookstorage.com/privacy");

    window.scrollTo(0, 0);
  }, []);

  return (
    <div class="min-h-screen bg-white text-slate-900">
      <PublicHeader />
      <div class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="mb-2 text-3xl font-bold tracking-tight">Privacy Policy</h1>
        <p class="mb-1 text-sm text-slate-500">
          Effective date: March 11, 2026
        </p>
        <p class="mb-10 text-sm text-slate-500">
          Last updated: March 11, 2026
        </p>

        <div class="space-y-8 text-sm leading-relaxed text-slate-700">

          {/* ── Section 1 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">1. Who We Are</h2>
            <p class="mb-3">
              WebhookStorage is operated by <strong>Sammons Software LLC</strong>, a Missouri limited liability company ("we," "us," "our"). We are based in O'Fallon, Missouri.
            </p>
            <p class="mb-3">
              For privacy-related inquiries, contact us at <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a>.
            </p>
            <p>
              For general support, contact us at <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a>.
            </p>
          </section>

          {/* ── Section 2 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">2. Our Role: Controller vs. Processor</h2>
            <p class="mb-3">We act in two distinct roles depending on the type of data:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>
                <strong>Controller:</strong> We are the data controller for Account Data — the information you provide to create and manage your account (your name, email, billing details). We decide how this data is collected and used.
              </li>
              <li>
                <strong>Processor:</strong> We are the data processor for Customer Data — the webhook payloads you store through the Service. You (the customer) decide what data is sent to your endpoints. We store it on your behalf without inspecting, parsing, or analyzing its content.
              </li>
            </ul>
            <p>
              This distinction matters because our obligations differ depending on the role. This Privacy Policy governs both. If you require a formal Data Processing Agreement for Customer Data, one is available in your account dashboard at <strong>webhookstorage.com/dashboard/legal</strong>.
            </p>
          </section>

          {/* ── Section 3 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">3. What We Collect</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.1 Account Data (We Control)</h3>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Data</th>
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Source</th>
                    <th class="pb-2 font-semibold text-slate-900">Purpose</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr><td class="py-2 pr-4">Email address</td><td class="py-2 pr-4">Provided at sign-up via WorkOS</td><td class="py-2">Account identity, transactional emails</td></tr>
                  <tr><td class="py-2 pr-4">Name</td><td class="py-2 pr-4">Provided at sign-up via WorkOS</td><td class="py-2">Account display, communications</td></tr>
                  <tr><td class="py-2 pr-4">Authentication events</td><td class="py-2 pr-4">WorkOS (magic link / passkey usage)</td><td class="py-2">Security, session management</td></tr>
                  <tr><td class="py-2 pr-4">IP address at login</td><td class="py-2 pr-4">Collected automatically</td><td class="py-2">Security, abuse prevention</td></tr>
                </tbody>
              </table>
            </div>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.2 Customer Data (You Control)</h3>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Data</th>
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Source</th>
                    <th class="pb-2 font-semibold text-slate-900">Purpose</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr><td class="py-2 pr-4">Webhook payload bodies</td><td class="py-2 pr-4">Sent by your configured webhook senders</td><td class="py-2">Stored and made available for your retrieval</td></tr>
                  <tr><td class="py-2 pr-4">Request metadata</td><td class="py-2 pr-4">Collected at ingest: sender IP, content-type header, payload size, timestamp</td><td class="py-2">Displayed in dashboard, used for billing</td></tr>
                </tbody>
              </table>
            </div>
            <p class="mb-4">We do not inspect, index, search, or analyze the content of webhook payloads. We do not know what data they contain.</p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.3 Usage and Billing Data</h3>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Data</th>
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Source</th>
                    <th class="pb-2 font-semibold text-slate-900">Purpose</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr><td class="py-2 pr-4">Ingest event counts</td><td class="py-2 pr-4">Metering infrastructure</td><td class="py-2">Billing (reported to Stripe)</td></tr>
                  <tr><td class="py-2 pr-4">Storage consumption (daily snapshots)</td><td class="py-2 pr-4">Metering infrastructure</td><td class="py-2">Billing (reported to Stripe)</td></tr>
                  <tr><td class="py-2 pr-4">API request logs</td><td class="py-2 pr-4">Application logs</td><td class="py-2">Service monitoring, debugging</td></tr>
                </tbody>
              </table>
            </div>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.4 Payment Data</h3>
            <p>
              Billing is handled entirely by <strong>Stripe</strong>. We do not receive, store, or have access to your credit card number or full payment method details. Stripe provides us with: last four digits of your card, card brand, expiration date, and billing address (for invoice display and tax purposes only). See <a href="https://stripe.com/privacy" class="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
            </p>
          </section>

          {/* ── Section 4 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">4. Legal Basis for Processing</h2>
            <p class="mb-4">
              We process your data under the following legal bases. For users in the European Economic Area (EEA), United Kingdom, or Switzerland, these correspond to GDPR Article 6 — however, please note that the Service is currently offered to US-based customers only (see Section 9).
            </p>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Processing Activity</th>
                    <th class="pb-2 font-semibold text-slate-900">Legal Basis</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr><td class="py-2 pr-4">Account creation and management</td><td class="py-2">Performance of contract (Art. 6(1)(b))</td></tr>
                  <tr><td class="py-2 pr-4">Storing and serving webhook payloads</td><td class="py-2">Performance of contract (Art. 6(1)(b))</td></tr>
                  <tr><td class="py-2 pr-4">Metered billing and payment processing</td><td class="py-2">Performance of contract (Art. 6(1)(b))</td></tr>
                  <tr><td class="py-2 pr-4">Transactional emails (billing, security)</td><td class="py-2">Performance of contract (Art. 6(1)(b))</td></tr>
                  <tr><td class="py-2 pr-4">Security monitoring and abuse prevention</td><td class="py-2">Legitimate interest (Art. 6(1)(f))</td></tr>
                  <tr><td class="py-2 pr-4">Service improvement and debugging</td><td class="py-2">Legitimate interest (Art. 6(1)(f))</td></tr>
                  <tr><td class="py-2 pr-4">Compliance with legal obligations</td><td class="py-2">Legal obligation (Art. 6(1)(c))</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              For Customer Data containing personal information of third parties (e.g., individuals whose data appears in webhook payloads), you as the data controller are responsible for establishing the legal basis for that processing.
            </p>
          </section>

          {/* ── Section 5 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">5. How We Use Your Data</h2>
            <p class="mb-3">We use your data to:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Provide and operate the WebhookStorage service.</li>
              <li>Process billing and report usage via Stripe metered billing.</li>
              <li>Send transactional emails related to your account (via Amazon SES). These include: sign-up confirmations, billing notifications, payment failure alerts, and security alerts.</li>
              <li>Monitor, troubleshoot, and improve the service.</li>
              <li>Enforce our Terms of Service and Acceptable Use Policy.</li>
              <li>Comply with applicable laws and respond to lawful requests from authorities.</li>
            </ul>
            <p class="mb-3"><strong>We do not:</strong></p>
            <ul class="ml-5 list-disc space-y-2">
              <li>Sell your personal data to third parties.</li>
              <li>Use your data for advertising or marketing purposes.</li>
              <li>Share your data with third parties except as described in Section 6.</li>
              <li>Use Customer Data (webhook payloads) for any purpose other than storing and serving it back to you.</li>
            </ul>
          </section>

          {/* ── Section 6 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">6. Third-Party Services</h2>
            <p class="mb-3">
              We use third-party services to operate the platform, including cloud infrastructure (AWS), authentication (WorkOS), payment processing (Stripe), and DNS/DDoS protection (Cloudflare). All are based in the United States. We do not authorize any third-party service to use your data for advertising or to sell it.
            </p>
            <p class="mb-3">
              For the complete list of subprocessors, including specific data shared and notification rights, see our{" "}
              <a href="/legal" class="text-teal-700 underline hover:text-teal-900">Data Processing Agreement</a>.
            </p>
            <p>
              If we add product analytics tools in the future, we will disclose them and ensure they operate as service providers — not to share data with advertisers or other third parties.
            </p>
          </section>

          {/* ── Section 7 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">7. Data Retention</h2>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Data Type</th>
                    <th class="pb-2 font-semibold text-slate-900">Retention Period</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr><td class="py-2 pr-4">Webhook payloads</td><td class="py-2">Configurable per endpoint: 1, 7, 30, 90, or 365 days. Default: 1 day. Automatically deleted when the retention period expires.</td></tr>
                  <tr><td class="py-2 pr-4">Payload metadata</td><td class="py-2">Deleted within 7 days of payload deletion.</td></tr>
                  <tr><td class="py-2 pr-4">Account data</td><td class="py-2">Retained while your account is active. Deleted within 30 days of account termination.</td></tr>
                  <tr><td class="py-2 pr-4">Billing records</td><td class="py-2">Retained for 90 days after account termination for audit and legal compliance.</td></tr>
                  <tr><td class="py-2 pr-4">API request logs</td><td class="py-2">Retained for 30 days for operational purposes.</td></tr>
                  <tr><td class="py-2 pr-4">Payment data (at Stripe)</td><td class="py-2">Subject to <a href="https://stripe.com/privacy" class="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe's retention policy</a>.</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              You may delete individual payloads at any time via the API or dashboard. You may request full account deletion by contacting <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a>.
            </p>
          </section>

          {/* ── Section 8 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">8. Data Security</h2>
            <p class="mb-3">We implement the following measures to protect your data:</p>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>Encryption at rest:</strong> All webhook payloads and metadata are encrypted using AES-256 via AWS Key Management Service (KMS).</li>
              <li><strong>Encryption in transit:</strong> All connections use TLS 1.2 or higher. The S3 bucket policy rejects non-HTTPS requests.</li>
              <li><strong>Tenant isolation:</strong> Each customer's data is stored with unique prefixes and partition keys. One tenant cannot access another tenant's data. Tenant identity is derived from authenticated tokens, not user-supplied headers.</li>
              <li><strong>Credential security:</strong> API keys and endpoint secrets are stored as one-way cryptographic hashes (SHA-256). We cannot retrieve your credentials — only you can rotate them.</li>
              <li><strong>Access control:</strong> Least-privilege IAM roles for all service components. Production data access is limited to authorized Sammons Software LLC personnel.</li>
              <li><strong>Download security:</strong> Payload download URLs are time-limited (15 minutes) and require authentication.</li>
            </ul>
          </section>

          {/* ── Section 9 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">9. International Data Transfers</h2>
            <p class="mb-3">
              All Customer Data and Account Data is stored and processed in the <strong>United States</strong> (AWS us-east-1, N. Virginia). All subprocessors listed in Section 6 are based in the United States.
            </p>
            <p class="mb-3">
              The Service is currently offered to customers located in the United States. We do not currently market to, or provide localized compliance support for, customers in the European Union, European Economic Area, United Kingdom, or Switzerland. If you access the Service from outside the United States, you do so at your own discretion and are responsible for compliance with your local data protection laws.
            </p>
            <p class="mb-3">
              Regardless of where you access the Service from, all data benefits from the security measures described in Section 8, including encryption at rest and in transit, tenant isolation, and the fact that we do not access webhook payload content during normal operations.
            </p>
            <p>
              If we expand the Service to actively support international customers in the future, we will update this Privacy Policy and our Data Processing Agreement to include appropriate transfer mechanisms (such as Standard Contractual Clauses).
            </p>
          </section>

          {/* ── Section 10 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">10. Your Rights</h2>
            <p class="mb-4">Depending on your location, you may have the following rights regarding your personal data:</p>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Right</th>
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Description</th>
                    <th class="pb-2 font-semibold text-slate-900">How to Exercise</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr>
                    <td class="py-2 pr-4"><strong>Access</strong></td>
                    <td class="py-2 pr-4">Request a copy of the personal data we hold about you</td>
                    <td class="py-2">Email <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a></td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Rectification</strong></td>
                    <td class="py-2 pr-4">Request correction of inaccurate personal data</td>
                    <td class="py-2">Email <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a></td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Erasure</strong></td>
                    <td class="py-2 pr-4">Request deletion of your personal data</td>
                    <td class="py-2">Delete payloads via dashboard/API; request account deletion via <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a></td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Portability</strong></td>
                    <td class="py-2 pr-4">Receive your data in a structured, machine-readable format</td>
                    <td class="py-2">Export webhook data via the API</td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Restriction</strong></td>
                    <td class="py-2 pr-4">Request that we limit processing of your data</td>
                    <td class="py-2">Email <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a></td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Objection</strong></td>
                    <td class="py-2 pr-4">Object to processing based on legitimate interest</td>
                    <td class="py-2">Email <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a></td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4"><strong>Withdraw consent</strong></td>
                    <td class="py-2 pr-4">Withdraw consent where processing is based on consent</td>
                    <td class="py-2">Email <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mb-3">
              We will respond to rights requests within <strong>thirty (30) days</strong>. If we need additional time (up to 60 days), we will notify you of the reason for the extension.
            </p>
            <p class="mb-3">
              If you are unsatisfied with our response, you have the right to lodge a complaint with your local data protection supervisory authority.
            </p>
            <p>
              <strong>For California residents:</strong> Under the CCPA, you have the right to know what personal information we collect and how we use it, to request deletion, and to not be discriminated against for exercising your rights. We do not sell personal information. To exercise your rights, contact <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a>.
            </p>
          </section>

          {/* ── Section 11 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">11. Cookies and Tracking</h2>
            <p class="mb-3">
              <strong>The Service does not set or use browser cookies.</strong> Authentication is managed through sessionStorage and Authorization headers, which do not persist beyond your browser session and are not transmitted automatically with requests.
            </p>
            <p class="mb-3"><strong>We do not use:</strong></p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Cookies of any kind (session, persistent, or third-party)</li>
              <li>Analytics or tracking scripts</li>
              <li>Third-party advertising pixels</li>
              <li>Social media tracking pixels</li>
              <li>Browser fingerprinting or similar tracking technologies</li>
            </ul>
            <p>
              <strong>Do Not Track:</strong> Because we do not track users in any way — not across websites, not within the Service — we do not respond to Do Not Track (DNT) browser signals. Our behavior is the same regardless of the DNT setting: we do not track.
            </p>
          </section>

          {/* ── Section 12 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">12. Children's Privacy</h2>
            <p>
              The Service is not directed at children under the age of thirteen (13). We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us at <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a> and we will promptly delete it.
            </p>
          </section>

          {/* ── Section 13 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">13. Data Breach Notification</h2>
            <p>
              If we become aware of a confirmed security breach affecting your personal data, we will notify you by email without unreasonable delay, and in no event later than seventy-two (72) hours after confirmation. Our notification will describe the nature of the breach, the data affected, and the measures we are taking to address it. For full details, see Section 8.6 of our Terms of Service.
            </p>
          </section>

          {/* ── Section 14 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">14. Changes to This Policy</h2>
            <p class="mb-3">
              We may update this Privacy Policy from time to time. Material changes will be communicated by email to the address associated with your account at least thirty (30) days before they take effect. We will also update the "Last Updated" date at the top of this page.
            </p>
            <p>
              Continued use of the Service after the effective date of changes constitutes acceptance. If you disagree with the changes, you may close your account before the effective date.
            </p>
          </section>

          {/* ── Section 15 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">15. Contact</h2>
            <p class="mb-3">For privacy-related questions or to exercise your data rights:</p>
            <p class="mb-4">
              <strong>Sammons Software LLC</strong><br />
              O'Fallon, Missouri<br />
              Email: <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a>
            </p>
            <p>
              For general support: <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a>
            </p>
          </section>

        </div>

      </div>
      <PublicFooter />
    </div>
  );
}
